import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";

export const Route = createFileRoute("/unsubscribe")({
  component: UnsubscribePage,
  head: () => ({
    meta: [
      { title: "Newsletter abbestellen | creategy" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
});

type State =
  | { kind: "loading" }
  | { kind: "valid" }
  | { kind: "already" }
  | { kind: "invalid" }
  | { kind: "submitting" }
  | { kind: "done" }
  | { kind: "error"; message: string };

function UnsubscribePage() {
  const [state, setState] = useState<State>({ kind: "loading" });
  const [token, setToken] = useState<string | null>(null);
  // Ticket VS3: Vermerk der Aussendung, aus der der Link stammt.
  const [broadcastId, setBroadcastId] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("token");
    setBroadcastId(params.get("b"));
    if (!t) {
      setState({ kind: "invalid" });
      return;
    }
    setToken(t);
    fetch(`/email/unsubscribe?token=${encodeURIComponent(t)}`)
      .then(async (res) => {
        const body = await res.json().catch(() => ({}));
        if (!res.ok) {
          setState({ kind: "invalid" });
          return;
        }
        if (body.valid) setState({ kind: "valid" });
        else if (body.reason === "already_unsubscribed")
          setState({ kind: "already" });
        else setState({ kind: "invalid" });
      })
      .catch(() =>
        setState({ kind: "error", message: "Verbindung fehlgeschlagen." }),
      );
  }, []);

  const confirm = async () => {
    if (!token) return;
    setState({ kind: "submitting" });
    try {
      const query = new URLSearchParams({ token });
      if (broadcastId) query.set("b", broadcastId);
      const res = await fetch(`/email/unsubscribe?${query.toString()}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok && body.success) setState({ kind: "done" });
      else if (body.reason === "already_unsubscribed")
        setState({ kind: "already" });
      else
        setState({
          kind: "error",
          message: body.error ?? "Bitte versuchen Sie es später erneut.",
        });
    } catch {
      setState({ kind: "error", message: "Verbindung fehlgeschlagen." });
    }
  };


  return (
    <main className="min-h-dvh bg-background flex items-center justify-center px-6 py-24">
      <div className="max-w-lg w-full">
        <h1 className="heading-h1 text-primary text-4xl md:text-5xl mb-6">
          E-Mails abbestellen
        </h1>

        {state.kind === "loading" && (
          <p className="text-body text-lg">Einen Moment bitte…</p>
        )}

        {state.kind === "valid" && (
          <>
            <p className="text-body text-lg mb-8">
              Bitte bestätigen Sie, dass Sie keine weiteren E-Mails von uns
              erhalten möchten.
            </p>
            <button
              type="button"
              onClick={confirm}
              className="inline-flex items-center justify-center rounded-full bg-brand-button px-8 py-3 text-base font-heading text-[#212121] transition hover:opacity-90"
            >
              Abmeldung bestätigen
            </button>
          </>
        )}

        {state.kind === "submitting" && (
          <p className="text-body text-lg">Wird verarbeitet…</p>
        )}

        {state.kind === "done" && (
          <p className="text-body text-lg">
            Sie sind erfolgreich abgemeldet. Sie erhalten keine weiteren E-Mails
            mehr von uns.
          </p>
        )}

        {state.kind === "already" && (
          <p className="text-body text-lg">
            Diese E-Mail-Adresse ist bereits abgemeldet.
          </p>
        )}

        {state.kind === "invalid" && (
          <p className="text-body text-lg">
            Der Abmelde-Link ist ungültig oder abgelaufen.
          </p>
        )}

        {state.kind === "error" && (
          <p className="text-body text-lg text-destructive">{state.message}</p>
        )}
      </div>
    </main>
  );
}
